import apiClient from './apiClient';
import { NOTIFICATION_ENDPOINTS } from '../constants/apiEndpoints';

export interface INotificationItem {
	id: string | number;
	title?: string;
	message?: string;
	body?: string;
	description?: string;
	is_read: boolean;
	type?: string;
	notification_type?: string;
	status?: string;
	data?: any;
	route?: string;
	url?: string;
	created_at?: string;
	createdAt?: string;
	read_at?: string | null;
	[key: string]: any;
}

export interface INotificationFilterParams {
	page?: number;
	limit?: number;
	is_read?: boolean | string;
}

export interface INotificationListResult {
	list: INotificationItem[];
	total: number;
	page: number;
	limit: number;
	unreadCount?: number;
}

// CACHE & DEDUPLICATION STATE
let inflightUnreadPromise: Promise<number> | null = null;
let lastUnreadFetchTime = 0;
let cachedUnreadCount = 0;
const UNREAD_CACHE_TTL_MS = 25000; // 25 seconds cache TTL to prevent redundant calls

const parseNum = (val: any): number | undefined => {
	if (typeof val === 'number' && !isNaN(val)) return val;
	if (typeof val === 'string' && /^\d+$/.test(val.trim())) {
		const parsed = parseInt(val.trim(), 10);
		if (!isNaN(parsed)) return parsed;
	}
	return undefined;
};

const extractNotificationList = (
	response: any,
	page = 1,
	limit = 10,
): INotificationListResult => {
	const raw = response?.data;
	let list: any[] = [];

	if (Array.isArray(raw)) {
		list = raw;
	} else if (Array.isArray(raw?.data)) {
		list = raw.data;
	} else if (Array.isArray(raw?.list)) {
		list = raw.list;
	} else if (Array.isArray(raw?.rows)) {
		list = raw.rows;
	} else if (Array.isArray(raw?.notifications)) {
		list = raw.notifications;
	} else if (Array.isArray(response?.result)) {
		list = response.result;
	} else if (Array.isArray(response?.notifications)) {
		list = response.notifications;
	} else if (Array.isArray(response?.rows)) {
		list = response.rows;
	}

	const normalizedList: INotificationItem[] = list.map((item) => {
		const rawIsRead = item.is_read ?? item.isRead ?? Boolean(item.read_at);
		const isRead = Boolean(rawIsRead === true || rawIsRead === 1 || rawIsRead === 'true');

		return {
			...item,
			id: item.id ?? item._id ?? Math.random().toString(),
			title: item.title || '',
			message: item.message || item.body || item.description || item.title || '',
			is_read: isRead,
			type: item.type || item.notification_type || item.status || '',
			created_at: item.created_at || item.createdAt || item.date || item.timestamp,
		};
	});

	const total =
		parseNum(raw?.total_document) ??
		parseNum(raw?.total) ??
		parseNum(raw?.count) ??
		parseNum(raw?.totalRows) ??
		parseNum(raw?.totalCount) ??
		parseNum(response?.total_document) ??
		parseNum(response?.total) ??
		parseNum(response?.count) ??
		parseNum(response?.result) ??
		normalizedList.length;

	const unreadCount =
		parseNum(raw?.unread_count) ??
		parseNum(raw?.unreadCount) ??
		parseNum(raw?.unread) ??
		parseNum(response?.unread_count) ??
		parseNum(response?.unreadCount) ??
		parseNum(response?.unread);

	return {
		list: normalizedList,
		total: total || normalizedList.length,
		page,
		limit,
		unreadCount,
	};
};

export const notificationService = {
	getAll: async (
		params: INotificationFilterParams = {},
	): Promise<INotificationListResult> => {
		const page = params.page ?? 1;
		const limit = params.limit ?? 10;
		const queryParams: Record<string, any> = {
			page,
			limit,
		};

		if (params.is_read !== undefined && params.is_read !== null) {
			queryParams.is_read = params.is_read;
		}

		const response = await apiClient(NOTIFICATION_ENDPOINTS.GET_ALL, {
			params: queryParams,
		});

		const result = extractNotificationList(response, page, limit);

		// Synchronize cached unread count if returned by get-all
		if (typeof result.unreadCount === 'number') {
			cachedUnreadCount = result.unreadCount;
			lastUnreadFetchTime = Date.now();
		}

		return result;
	},

	getUnreadCount: async (force = false): Promise<number> => {
		const now = Date.now();

		// 1. If an identical request is already inflight, share the promise (prevents 2-3 simultaneous calls)
		if (!force && inflightUnreadPromise) {
			return inflightUnreadPromise;
		}

		// 2. If recently fetched within TTL, return cached count with 0 network calls
		if (!force && now - lastUnreadFetchTime < UNREAD_CACHE_TTL_MS) {
			return cachedUnreadCount;
		}

		inflightUnreadPromise = (async () => {
			try {
				const response = await apiClient(NOTIFICATION_ENDPOINTS.UNREAD_COUNT);
				const raw = response?.data;

				const candidates = [
					parseNum(raw),
					parseNum(raw?.unread_count),
					parseNum(raw?.unreadCount),
					parseNum(raw?.count),
					parseNum(raw?.unread),
					parseNum(response?.unread_count),
					parseNum(response?.unreadCount),
					parseNum(response?.count),
					parseNum(response?.unread),
					parseNum(response?.result),
				];

				let count = 0;
				for (const c of candidates) {
					if (typeof c === 'number' && !isNaN(c)) {
						count = c;
						break;
					}
				}

				cachedUnreadCount = count;
				lastUnreadFetchTime = Date.now();
				return count;
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Failed to get unread notification count:', error);
				return cachedUnreadCount;
			} finally {
				inflightUnreadPromise = null;
			}
		})();

		return inflightUnreadPromise;
	},

	setCachedUnreadCount: (count: number) => {
		cachedUnreadCount = Math.max(0, count);
		lastUnreadFetchTime = Date.now();
	},

	update: async (
		id: string | number,
		payload: any = { is_read: true },
	): Promise<any> => {
		const res = await apiClient(NOTIFICATION_ENDPOINTS.UPDATE(id), {
			method: 'PATCH',
			body: payload,
		});
		if (payload?.is_read) {
			cachedUnreadCount = Math.max(0, cachedUnreadCount - 1);
			lastUnreadFetchTime = Date.now();
		}
		return res;
	},

	markAllRead: async (): Promise<any> => {
		const res = await apiClient(NOTIFICATION_ENDPOINTS.MARK_ALL_READ, {
			method: 'PATCH',
		});
		cachedUnreadCount = 0;
		lastUnreadFetchTime = Date.now();
		return res;
	},
};

export default notificationService;
